import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useModalA11y } from "@/hooks/use-modal-a11y";

import { Button } from "./button";
import { Skeleton } from "./skeleton";

interface BuyNowConfirmationModalProps {
	open: boolean;
	loading?: boolean;
	onClose: () => void;
	onCheckout: (quantity: number) => void;
	product: {
		id: string;
		name: string;
		price: number;
		images: string[];
		vendorName?: string;
		quantity?: number;
	};
	bagId: string;
}

const BuyNowConfirmationModal: React.FC<BuyNowConfirmationModalProps> = ({
	open,
	loading = false,
	onClose,
	onCheckout,
	product
}) => {
	const modalRef = useRef<HTMLDivElement>(null);
	const [quantity, setQuantity] = useState(product.quantity || 1);
	useModalA11y(open, modalRef);

	// Reset quantity when modal opens with new product
	useEffect(() => {
		if (open) {
			setQuantity(product.quantity || 1);
		}
	}, [open, product.quantity]);

	const handleIncrement = () => {
		setQuantity(prev => prev + 1);
	};

	const handleDecrement = () => {
		if (quantity > 1) {
			setQuantity(prev => prev - 1);
		}
	};

	if (!open)
		return null;

	const totalPrice = product.price * quantity;

	return createPortal(
		<div
			ref={modalRef}
			role="dialog"
			aria-modal="true"
			aria-labelledby="buy-now-title"
			className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn"
			onClick={onClose}
			tabIndex={-1}
			onKeyDown={(e) => {
				if (e.key === "Escape") {
					onClose();
				}
			}}
		>
			<div
				className="relative bg-white shadow-2xl border border-[#ececff] animate-scaleIn p-6 rounded-2xl w-full max-w-md"
				onClick={e => e.stopPropagation()}
				style={{
					boxShadow: "0 8px 32px rgba(63,46,200,0.12)",
					margin: "12vh 0",
					minHeight: "auto",
					maxHeight: "76vh",
					overflowY: "auto"
				}}
			>
				{/* Close button */}
				<button
					className="rounded-full p-1 transition-colors duration-150 hover:bg-gray-200"
					style={{
						position: "absolute",
						top: 12,
						right: 16,
						lineHeight: 1,
						border: "none",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						zIndex: 10
					}}
					onClick={onClose}
					aria-label="Close"
				>
					<img
						src="/assets/icons/close.svg"
						alt="Close"
						style={{ width: 24, height: 24, display: "block" }}
					/>
				</button>

				{/* Title */}
				<h2 id="buy-now-title" className="text-2xl font-bold text-[#3F2EC8] mb-4 text-center">Proceed to Checkout?</h2>

				{loading
					? (
							<>
								{/* Skeleton Loading State */}
								<div className="bg-gray-50 rounded-lg p-4 mb-4">
									<Skeleton className="w-full h-48 rounded-lg mb-3" />
									<Skeleton className="h-4 w-24 mb-2" />
									<Skeleton className="h-5 w-3/4 mb-2" />
									<div className="flex justify-between items-center mb-2">
										<Skeleton className="h-5 w-28" />
										<Skeleton className="h-4 w-20" />
									</div>
									<div className="flex justify-between items-center pt-3 border-t border-gray-200">
										<Skeleton className="h-5 w-16" />
										<Skeleton className="h-6 w-24" />
									</div>
								</div>
								<div className="flex flex-col gap-3">
									<Skeleton className="h-10 w-full rounded-md" />
									<Skeleton className="h-10 w-full rounded-md" />
								</div>
							</>
						)
					: (
							<>
								{/* Product Card - Similar to vendor bag card */}
								<div className="bg-gray-50 rounded-lg p-4 mb-4">
									{/* Product Image */}
									{product.images && product.images[0] && (
										<img
											src={product.images[0]}
											alt={product.name}
											className="w-full h-48 object-cover rounded-lg mb-3"
										/>
									)}

									{/* Vendor Name */}
									{product.vendorName && (
										<div className="text-sm text-gray-600 mb-2">
											Vendor:
											{" "}
											{product.vendorName}
										</div>
									)}

									{/* Product Name */}
									<h3 className="font-medium text-lg text-[#2A2A2A] mb-2">{product.name}</h3>

									{/* Quantity Controls and Price */}
									<div className="flex justify-between items-center mb-2">
										<div className="flex items-center gap-2">
											<span className="text-sm text-gray-600">Quantity:</span>
											<div className="flex items-center gap-1 bg-white border border-[#B3B3B3] rounded-[4px] px-1 h-[22px] min-w-[60px]">
												<button
													onClick={handleDecrement}
													disabled={quantity <= 1}
													className="text-gray-500 w-5 h-5 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
													aria-label="Decrease quantity"
												>
													-
												</button>
												<span className="mx-1 min-w-[14px] text-center">{quantity}</span>
												<button
													onClick={handleIncrement}
													className="text-gray-500 w-5 h-5 flex items-center justify-center"
													aria-label="Increase quantity"
												>
													+
												</button>
											</div>
										</div>
										<span className="text-sm text-gray-600">
											₦
											{product.price.toLocaleString()}
											/unit
										</span>
									</div>

									{/* Total Price */}
									<div className="flex justify-between items-center pt-3 border-t border-gray-200">
										<span className="font-semibold text-gray-700">Total:</span>
										<span className="font-bold text-lg text-[#3F2EC8]">
											₦
											{totalPrice.toLocaleString()}
										</span>
									</div>
								</div>

								{/* Action Buttons */}
								<div className="flex flex-col gap-3">
									<Button
										className="w-full bg-[#3F2EC8] hover:bg-[#2F1EA8] text-white"
										onClick={() => onCheckout(quantity)}
									>
										Go to Checkout
									</Button>
									<Button
										className="w-full"
										variant="outline"
										onClick={onClose}
									>
										Cancel
									</Button>
								</div>
							</>
						)}
			</div>
		</div>,
		document.body
	);
};

export default BuyNowConfirmationModal;
